'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import {
  ChevronDown,
  ChevronRight,
  Filter,
  Globe,
  Loader2,
  MessageSquare,
  PanelLeftClose,
  Phone,
  RefreshCw,
  ShieldCheck,
  Users,
} from 'lucide-react'
import { useRouter } from '@/i18n/navigation'
import { api } from '@/lib/api'
import type {
  AgentAssignedSessionSummary,
  AgentAssignedSessionsPage,
} from '@/types'
import { cn, buildAgentDashboardChatUrl } from '@/lib/utils'
import { useDebounce } from '@/hooks/use-api'
import {
  deriveSessionContactIdentifiers,
  extractContactIdentifiers,
  getContactDisplayName,
  normalizeForComparison,
} from '@/lib/agent-contact-utils'
import type { AgentContactRecord } from '@/lib/agent-contact-utils'
import { useAgentContactMatches } from '@/hooks/use-agent-contact-matches'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  CHANNEL_CONFIGS,
  CHANNEL_ALIAS_TO_SLUG,
  CHANNEL_ALIASES_BY_SLUG,
  CHANNEL_LABEL_BY_SLUG,
  type StandardCommunicationChannel,
} from '@/constants/channels'
import { useAssignedSessionsFiltersStore } from '@/stores/assignedSessionsFilters'

interface AssignedSessionsSidebarProps {
  currentAgentId?: number | null
  includeTeamSessions?: boolean
  teamKey: string
  onSessionSelect?: (sessionId: string) => void
  onCollapse?: () => void
}

const PAGE_SIZE = 30

type ContactFilterOptionSource = 'remote' | 'selected' | 'recent'

type ContactFilterOption = {
  identifier: string
  primary: string
  secondary?: string
  normalizedKey: string
  source: ContactFilterOptionSource
}
const formatRelativeTime = (timestamp: string | null): string => {
  if (!timestamp) {
    return 'No recent activity'
  }

  const date = new Date(timestamp)
  if (Number.isNaN(date.getTime())) {
    return 'No recent activity'
  }

  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMinutes = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffMinutes < 1) {
    return 'Just now'
  }
  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`
  }
  if (diffHours < 24) {
    return `${diffHours}h ago`
  }
  if (diffDays < 7) {
    return `${diffDays}d ago`
  }

  const dateFormatOptions: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }
  if (date.getUTCFullYear() !== now.getUTCFullYear()) {
    dateFormatOptions.year = 'numeric'
  }

  return new Intl.DateTimeFormat('en-US', dateFormatOptions).format(date)
}

const formatPlatformLabel = (platform: string): string => {
  const normalized = platform.toLowerCase()
  const slug = CHANNEL_ALIAS_TO_SLUG[normalized]
  if (slug) {
    return CHANNEL_LABEL_BY_SLUG[slug]
  }

  if (!platform) {
    return 'Unknown'
  }

  const friendly = platform.replace(/[_-]+/g, ' ').trim()
  if (!friendly) {
    return 'Unknown'
  }

  return friendly.charAt(0).toUpperCase() + friendly.slice(1)
}

const getPlatformIcon = (platform: string) => {
  switch (platform) {
    case 'whatsapp':
    case 'sms':
      return Phone
    case 'facebook':
    case 'facebook_messenger':
    case 'instagram':
    case 'telegram':
      return Users
    default:
      return Globe
  }
}

const getSenderLabel = (
  sender?: 'agent' | 'guest' | 'bot' | 'system' | null
): string | null => {
  switch (sender) {
    case 'agent':
      return 'Agent'
    case 'guest':
      return 'Guest'
    case 'bot':
      return 'Bot'
    case 'system':
      return 'System'
    default:
      return null
  }
}

export function AssignedSessionsSidebar({
  currentAgentId,
  includeTeamSessions = true,
  teamKey,
  onSessionSelect,
  onCollapse,
}: AssignedSessionsSidebarProps) {
  const router = useRouter()
  const loadMoreRef = useRef<HTMLDivElement | null>(null)
  const channelFilters = useAssignedSessionsFiltersStore(
    state => state.channelFilters
  )
  const agentFilters = useAssignedSessionsFiltersStore(
    state => state.agentFilters
  )
  const contactFilters = useAssignedSessionsFiltersStore(
    state => state.contactFilters
  )
  const includeUnassigned = useAssignedSessionsFiltersStore(
    state => state.includeUnassigned
  )
  const dateFrom = useAssignedSessionsFiltersStore(state => state.dateFrom)
  const dateTo = useAssignedSessionsFiltersStore(state => state.dateTo)
  const contactSearch = useAssignedSessionsFiltersStore(
    state => state.contactSearch
  )
  const areFiltersCollapsed = useAssignedSessionsFiltersStore(
    state => state.areFiltersCollapsed
  )
  const toggleChannelFilter = useAssignedSessionsFiltersStore(
    state => state.toggleChannelFilter
  )
  const toggleAgentFilter = useAssignedSessionsFiltersStore(
    state => state.toggleAgentFilter
  )
  const toggleContactFilter = useAssignedSessionsFiltersStore(
    state => state.toggleContactFilter
  )
  const setIncludeUnassigned = useAssignedSessionsFiltersStore(
    state => state.setIncludeUnassigned
  )
  const setDateFromValue = useAssignedSessionsFiltersStore(
    state => state.setDateFrom
  )
  const setDateToValue = useAssignedSessionsFiltersStore(
    state => state.setDateTo
  )
  const setContactSearchValue = useAssignedSessionsFiltersStore(
    state => state.setContactSearch
  )
  const setFiltersCollapsed = useAssignedSessionsFiltersStore(
    state => state.setFiltersCollapsed
  )
  const resetFilters = useAssignedSessionsFiltersStore(
    state => state.resetFilters
  )
  const isClientReady = typeof window !== 'undefined'
  const [isContactDropdownOpen, setIsContactDropdownOpen] = useState(false)
  const [contactPickerSearch, setContactPickerSearch] = useState('')
  const debouncedContactPickerSearch = useDebounce(contactPickerSearch, 300)

  const sortedChannelFilters = useMemo(
    () => [...channelFilters].sort(),
    [channelFilters]
  )
  const sortedAgentFilters = useMemo(
    () => [...agentFilters].sort((a, b) => a - b),
    [agentFilters]
  )
  const sortedContactFilters = useMemo(
    () => [...contactFilters].sort(),
    [contactFilters]
  )
  const contactFilterSet = useMemo(
    () => new Set(sortedContactFilters),
    [sortedContactFilters]
  )
  const normalizedContactFilterSet = useMemo(() => {
    const normalizedSet = new Set<string>()
    sortedContactFilters.forEach(value => {
      const normalized = normalizeForComparison(value)
      if (normalized) {
        normalizedSet.add(normalized)
      }
    })
    return normalizedSet
  }, [sortedContactFilters])
  const channelFilterCount = sortedChannelFilters.length
  const agentFilterCount = sortedAgentFilters.length
  const contactFilterCount = sortedContactFilters.length

  const handleContactDropdownOpenChange = (open: boolean) => {
    setIsContactDropdownOpen(open)
    if (!open) {
      setContactPickerSearch('')
    }
  }
  const trimmedSearch = useMemo(() => {
    const raw = contactSearch ?? ''
    return raw.trim()
  }, [contactSearch])

  const resolvedChannelFilters = useMemo(() => {
    if (sortedChannelFilters.length === 0) {
      return []
    }
    const aliasSet = new Set<string>()
    sortedChannelFilters.forEach(slug => {
      const aliases = CHANNEL_ALIASES_BY_SLUG[slug] ?? [slug]
      aliases.forEach(alias => aliasSet.add(alias))
    })
    return Array.from(aliasSet)
  }, [sortedChannelFilters])

  const filterParams = useMemo(() => {
    const params = {
      channels: resolvedChannelFilters,
      agentIds: sortedAgentFilters,
      contactIds: sortedContactFilters,
      includeUnassigned,
      dateFrom,
      dateTo,
      search: trimmedSearch,
    }
    console.log('🔍 [DEBUG] Filter params built:', {
      sortedContactFilters,
      contactFilterCount,
      hasContactIds: params.contactIds && params.contactIds.length > 0,
      params,
    })
    return params
  }, [
    resolvedChannelFilters,
    sortedAgentFilters,
    sortedContactFilters,
    contactFilterCount,
    includeUnassigned,
    dateFrom,
    dateTo,
    trimmedSearch,
  ])
  const filterKey = useMemo(() => JSON.stringify(filterParams), [filterParams])

  const {
    data,
    status,
    error,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useInfiniteQuery<AgentAssignedSessionsPage, Error>({
    queryKey: [
      'agent-dashboard',
      teamKey,
      'assigned-sessions',
      includeTeamSessions ? 'team' : 'agent-only',
      filterKey,
    ],
    initialPageParam: 1,
    getNextPageParam: lastPage =>
      lastPage.hasMore ? lastPage.page + 1 : undefined,
    queryFn: async ({ pageParam }) => {
      const dateFromIso = filterParams.dateFrom
        ? new Date(`${filterParams.dateFrom}T00:00:00.000Z`).toISOString()
        : undefined
      const dateToIso = filterParams.dateTo
        ? new Date(`${filterParams.dateTo}T23:59:59.999Z`).toISOString()
        : undefined

      const response = await api.sessions.getTeamSessions({
        page: typeof pageParam === 'number' ? pageParam : 1,
        limit: PAGE_SIZE,
        includeTeam: includeTeamSessions,
        channels:
          filterParams.channels.length > 0 ? filterParams.channels : undefined,
        agentIds: filterParams.agentIds,
        contactIds:
          filterParams.contactIds.length > 0
            ? filterParams.contactIds
            : undefined,
        includeUnassigned: filterParams.includeUnassigned,
        dateFrom: dateFromIso,
        dateTo: dateToIso,
        search:
          filterParams.search && filterParams.search.length > 0
            ? filterParams.search
            : undefined,
      })

      if (!response.success || !response.data) {
        const message =
          response.error || 'Failed to load assigned chat sessions'
        throw new Error(message)
      }

      return response.data
    },
    staleTime: 10_000,
  })

  const allSessions = useMemo<AgentAssignedSessionSummary[]>(() => {
    if (!data?.pages?.length) {
      return []
    }
    return data.pages.flatMap(page => page.sessions)
  }, [data])

  const channelsInUse = useMemo(() => {
    const channelSet = new Set<StandardCommunicationChannel>()
    allSessions.forEach(session => {
      if (session.platform) {
        const normalized = session.platform.toLowerCase()
        const slug = CHANNEL_ALIAS_TO_SLUG[normalized]
        if (slug) {
          channelSet.add(slug)
        }
      }
    })
    return channelSet
  }, [allSessions])

  const availableAgents = useMemo(() => {
    const agentMap = new Map<number, string>()
    allSessions.forEach(session => {
      if (
        typeof session.assignedAgentId === 'number' &&
        Number.isFinite(session.assignedAgentId)
      ) {
        const name =
          session.assignedAgentName &&
          session.assignedAgentName.trim().length > 0
            ? session.assignedAgentName
            : `Agent ${session.assignedAgentId}`
        if (!agentMap.has(session.assignedAgentId)) {
          agentMap.set(session.assignedAgentId, name)
        }
      }
    })
    return Array.from(agentMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [allSessions])

  const availableContacts = useMemo(() => {
    const contactMap = new Map<string, string>()

    console.log(
      '🔍 [DEBUG] Processing sessions for contacts:',
      allSessions.length
    )

    allSessions.forEach(session => {
      // Use a more direct approach - extract the actual values from the session
      const possibleIdentifiers = [
        session.platformId,
        session.userId,
        session.contactAddress,
      ].filter(id => id && id.toString().trim())

      // console.log('🔍 [DEBUG] Session contact data:', {
      //   sessionId: session.id,
      //   platformId: session.platformId,
      //   userId: session.userId,
      //   contactAddress: session.contactAddress,
      //   possibleIdentifiers,
      // })

      possibleIdentifiers.forEach(identifier => {
        const idStr = identifier.toString()
        if (!contactMap.has(idStr)) {
          const name = session.displayName || session.contactAddress || idStr
          contactMap.set(idStr, name)
        }
      })
    })

    const contacts = Array.from(contactMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name))

    console.log('🔍 [DEBUG] Final available contacts:', contacts)

    return contacts
  }, [allSessions])
  const availableContactsByNormalizedId = useMemo(() => {
    const map = new Map<string, { id: string; name: string }>()
    availableContacts.forEach(contact => {
      const normalized = normalizeForComparison(contact.id) ?? contact.id
      map.set(normalized, contact)
    })
    return map
  }, [availableContacts])
  const {
    data: remoteContactRecords,
    isFetching: isFetchingRemoteContacts,
    isError: isRemoteContactError,
    error: remoteContactError,
  } = useQuery<AgentContactRecord[]>({
    queryKey: [
      'agent-dashboard',
      teamKey,
      'contact-filter-search',
      debouncedContactPickerSearch,
    ],
    enabled: isClientReady && isContactDropdownOpen,
    staleTime: 30_000,
    queryFn: async () => {
      const searchTerm = debouncedContactPickerSearch.trim()
      const response = await api.sessions.searchContacts({
        search: searchTerm.length > 0 ? searchTerm : undefined,
        limit: 25,
        withPhoneOnly: false,
      })

      if (!response.success) {
        throw new Error(response.error || 'Failed to load contacts')
      }

      const contacts = Array.isArray(response.data?.contacts)
        ? (response.data.contacts as AgentContactRecord[])
        : []

      return contacts
    },
  })
  const remoteContacts = useMemo(
    () => remoteContactRecords ?? [],
    [remoteContactRecords]
  )
  const remoteContactOptions = useMemo<ContactFilterOption[]>(() => {
    if (!remoteContacts.length) {
      return []
    }

    const seen = new Set<string>()
    const options: ContactFilterOption[] = []

    remoteContacts.forEach(contact => {
      const identifiers = extractContactIdentifiers(contact)
      if (identifiers.length === 0) {
        return
      }

      const displayName = getContactDisplayName(contact)

      identifiers.forEach(identifier => {
        if (!identifier) {
          return
        }
        const normalizedKey = normalizeForComparison(identifier) ?? identifier
        if (seen.has(normalizedKey)) {
          return
        }
        seen.add(normalizedKey)
        options.push({
          identifier,
          primary: displayName ?? identifier,
          secondary:
            displayName && displayName !== identifier ? identifier : undefined,
          normalizedKey,
          source: 'remote',
        })
      })
    })

    return options
  }, [remoteContacts])
  const remoteNormalizedKeys = useMemo(
    () => new Set(remoteContactOptions.map(option => option.normalizedKey)),
    [remoteContactOptions]
  )

  const activeFilterCount = useMemo(() => {
    let count = 0
    count += channelFilterCount
    count += agentFilterCount
    count += contactFilterCount
    if (!includeUnassigned) count += 1
    if (dateFrom) count += 1
    if (dateTo) count += 1
    if (trimmedSearch.length > 0) count += 1
    return count
  }, [
    channelFilterCount,
    agentFilterCount,
    contactFilterCount,
    includeUnassigned,
    dateFrom,
    dateTo,
    trimmedSearch,
  ])

  const handleChannelToggle = useCallback(
    (slug: StandardCommunicationChannel) => {
      toggleChannelFilter(slug)
    },
    [toggleChannelFilter]
  )

  const handleAgentToggle = useCallback(
    (agentId: number) => {
      toggleAgentFilter(agentId)
    },
    [toggleAgentFilter]
  )

  const handleContactToggle = useCallback(
    (contactId: string) => {
      console.log('🔍 [DEBUG] Contact filter toggled:', {
        contactId,
        currentFilters: contactFilters,
      })
      toggleContactFilter(contactId)
    },
    [toggleContactFilter, contactFilters]
  )

  const handleResetFilters = useCallback(() => {
    resetFilters()
  }, [resetFilters])

  const orderedSessions = useMemo<AgentAssignedSessionSummary[]>(() => {
    if (!allSessions.length) {
      return []
    }

    return [...allSessions].sort((a, b) => {
      const aTimestamp = a.lastMessageAt ?? a.createdAt ?? null
      const bTimestamp = b.lastMessageAt ?? b.createdAt ?? null

      if (!aTimestamp && !bTimestamp) {
        return 0
      }
      if (!aTimestamp) {
        return 1
      }
      if (!bTimestamp) {
        return -1
      }

      const aTime = new Date(aTimestamp).getTime()
      const bTime = new Date(bTimestamp).getTime()

      if (Number.isNaN(aTime) && Number.isNaN(bTime)) {
        return 0
      }
      if (Number.isNaN(aTime)) {
        return 1
      }
      if (Number.isNaN(bTime)) {
        return -1
      }

      return bTime - aTime
    })
  }, [allSessions])

  const sessionContactIdentifiers = useMemo(() => {
    if (!orderedSessions.length) {
      return [] as string[]
    }

    return orderedSessions.flatMap(session =>
      deriveSessionContactIdentifiers({
        platform: session.platform,
        contactAddress: session.contactAddress,
        platformId: session.platformId,
        userId: session.userId,
      })
    )
  }, [orderedSessions])

  const { data: contactMatches } = useAgentContactMatches(
    sessionContactIdentifiers
  )

  const displaySessions = useMemo<AgentAssignedSessionSummary[]>(() => {
    if (!orderedSessions.length) {
      return []
    }

    const hasActiveContactFilters = contactFilterSet.size > 0

    return orderedSessions
      .map(session => {
        const identifiers = deriveSessionContactIdentifiers({
          platform: session.platform,
          contactAddress: session.contactAddress,
          platformId: session.platformId,
          userId: session.userId,
        })

        const matchesContactFilter =
          !hasActiveContactFilters ||
          identifiers.some(identifier => {
            if (!identifier) {
              return false
            }

            if (contactFilterSet.has(identifier)) {
              return true
            }

            const normalized = normalizeForComparison(identifier)
            return normalized
              ? normalizedContactFilterSet.has(normalized)
              : false
          })

        if (!matchesContactFilter) {
          return null
        }

        if (contactMatches && identifiers.length) {
          for (const identifier of identifiers) {
            const normalized = normalizeForComparison(identifier)
            if (!normalized) {
              continue
            }

            const contact = contactMatches[normalized]
            if (!contact) {
              continue
            }

            const resolvedName = getContactDisplayName(contact)
            if (resolvedName) {
              return {
                ...session,
                displayName: resolvedName,
              }
            }
          }
        }

        return session
      })
      .filter(
        (session): session is AgentAssignedSessionSummary => session !== null
      )
  }, [
    orderedSessions,
    contactMatches,
    contactFilterSet,
    normalizedContactFilterSet,
  ])
  const fallbackContactOptions = useMemo<ContactFilterOption[]>(() => {
    if (contactFilters.length === 0 && availableContacts.length === 0) {
      return []
    }

    const options: ContactFilterOption[] = []
    const seen = new Set<string>(remoteNormalizedKeys)

    contactFilters.forEach(identifier => {
      const normalizedKey = normalizeForComparison(identifier) ?? identifier
      if (seen.has(normalizedKey)) {
        return
      }

      let primary = identifier
      let secondary: string | undefined
      const matchedContact =
        normalizedKey && contactMatches ? contactMatches[normalizedKey] : null

      if (matchedContact) {
        const displayName = getContactDisplayName(matchedContact)
        if (displayName) {
          primary = displayName
          if (displayName !== identifier) {
            secondary = identifier
          }
        }
      } else {
        const availableMatch =
          availableContactsByNormalizedId.get(normalizedKey)
        if (availableMatch) {
          primary = availableMatch.name
          if (availableMatch.name !== identifier) {
            secondary = identifier
          }
        }
      }

      options.push({
        identifier,
        primary,
        secondary,
        normalizedKey,
        source: 'selected',
      })
      seen.add(normalizedKey)
    })

    availableContacts.forEach(contact => {
      const identifier = contact.id
      const normalizedKey = normalizeForComparison(identifier) ?? identifier
      if (seen.has(normalizedKey)) {
        return
      }

      options.push({
        identifier,
        primary: contact.name,
        secondary: contact.name !== identifier ? identifier : undefined,
        normalizedKey,
        source: 'recent',
      })
      seen.add(normalizedKey)
    })

    return options
  }, [
    contactFilters,
    availableContacts,
    contactMatches,
    remoteNormalizedKeys,
    availableContactsByNormalizedId,
  ])
  const selectedFallbackOptions = useMemo(
    () => fallbackContactOptions.filter(option => option.source === 'selected'),
    [fallbackContactOptions]
  )
  const recentFallbackOptions = useMemo(
    () => fallbackContactOptions.filter(option => option.source === 'recent'),
    [fallbackContactOptions]
  )
  const trimmedContactPickerSearch = contactPickerSearch.trim()
  const hasContactPickerSearch = trimmedContactPickerSearch.length > 0
  const contactSearchErrorMessage =
    isRemoteContactError && remoteContactError
      ? remoteContactError instanceof Error
        ? remoteContactError.message
        : typeof remoteContactError === 'string'
          ? remoteContactError
          : 'Failed to load contacts'
      : null
  const renderContactOption = (option: ContactFilterOption) => (
    <DropdownMenuCheckboxItem
      key={`${option.source}-${option.normalizedKey}`}
      checked={contactFilterSet.has(option.identifier)}
      onCheckedChange={() => handleContactToggle(option.identifier)}
      className="whitespace-normal py-2 pl-8 pr-2 text-sm leading-tight"
    >
      <div className="flex flex-col gap-0.5">
        <span className="font-medium text-gray-900">{option.primary}</span>
        {option.secondary ? (
          <span className="text-xs text-gray-500">{option.secondary}</span>
        ) : null}
      </div>
    </DropdownMenuCheckboxItem>
  )

  useEffect(() => {
    const node = loadMoreRef.current
    if (!node || !hasNextPage) {
      return
    }

    const observer = new IntersectionObserver(entries => {
      const entry = entries[0]
      if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage().catch(fetchError => {
          console.error('Failed to load more assigned sessions:', fetchError)
        })
      }
    })

    observer.observe(node)

    return () => {
      observer.disconnect()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const handleSessionClick = (sessionId: string) => {
    if (onSessionSelect) {
      onSessionSelect(sessionId)
      return
    }

    router.push(buildAgentDashboardChatUrl(sessionId))
  }

  return (
    <div className="bg-white shadow rounded-lg flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Chat Sessions</h3>
          <p className="text-xs text-gray-500">
            {includeTeamSessions
              ? 'Sessions assigned to you or your team'
              : 'Sessions assigned to you'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {onCollapse && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={onCollapse}
              title="Collapse sidebar"
            >
              <PanelLeftClose className="h-4 w-4" />
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8"
            aria-expanded={!areFiltersCollapsed}
            aria-controls={`${teamKey}-filters`}
            onClick={() => setFiltersCollapsed(!areFiltersCollapsed)}
          >
            <Filter className="mr-2 h-3.5 w-3.5" />
            {/* dont show this */}
            {/* {areFiltersCollapsed ? 'Show filters' : 'Hide filters'} */}
            {activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
            {areFiltersCollapsed ? (
              <ChevronRight className="ml-2 h-3.5 w-3.5" />
            ) : (
              <ChevronDown className="ml-2 h-3.5 w-3.5" />
            )}
          </Button>
          <button
            type="button"
            onClick={() => {
              refetch().catch(fetchError => {
                console.error(
                  'Failed to refresh assigned sessions:',
                  fetchError
                )
              })
            }}
            className={cn(
              'inline-flex items-center rounded-md border px-2 py-1 text-xs font-medium transition',
              'border-gray-200 text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500'
            )}
            disabled={isFetching}
            aria-label="Refresh assigned sessions"
          >
            <RefreshCw
              className={cn(
                'h-4 w-4',
                isFetching ? 'animate-spin text-blue-500' : 'text-gray-500'
              )}
            />
          </button>
        </div>
      </div>

      {areFiltersCollapsed ? (
        <div
          id={`${teamKey}-filters`}
          className="border-b px-4 py-3 text-xs text-gray-500 flex items-center justify-between"
        >
          <span>
            {activeFilterCount > 0
              ? `${activeFilterCount} active filter${activeFilterCount === 1 ? '' : 's'}`
              : 'Filters hidden'}
          </span>
          {activeFilterCount > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-blue-600 hover:text-blue-700"
              onClick={handleResetFilters}
            >
              Clear
            </Button>
          )}
        </div>
      ) : (
        <div id={`${teamKey}-filters`} className="border-b px-4 py-3 space-y-3">
          <div className="flex items-center gap-2">
            <Input
              value={contactSearch}
              onChange={event => setContactSearchValue(event.target.value)}
              placeholder="Search by contact, email, phone, or session ID"
              className="h-8 text-sm"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleResetFilters}
              disabled={
                activeFilterCount === 0 &&
                includeUnassigned &&
                !dateFrom &&
                !dateTo &&
                trimmedSearch.length === 0
              }
              className="text-xs text-gray-600 hover:text-gray-900"
            >
              <Filter className="mr-1 h-3.5 w-3.5" />
              Reset
              {activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-gray-600">
              Channels
            </span>
            {CHANNEL_CONFIGS.map(channel => {
              const { slug, label } = channel
              const isActive = channelFilters.includes(slug)
              const isAvailable = channelsInUse.has(slug)
              return (
                <Button
                  key={slug}
                  type="button"
                  variant={isActive ? 'default' : 'outline'}
                  size="sm"
                  className={cn(
                    'h-7 whitespace-nowrap',
                    !isAvailable && !isActive && 'opacity-60'
                  )}
                  onClick={() => handleChannelToggle(slug)}
                >
                  {label}
                </Button>
              )
            })}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-gray-600">Agents</span>
            {isClientReady ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7"
                  >
                    {agentFilters.length > 0
                      ? `Selected (${agentFilters.length})`
                      : 'All agents'}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  <DropdownMenuLabel>Assigned agents</DropdownMenuLabel>
                  {availableAgents.length === 0 ? (
                    <div className="px-2 py-1.5 text-xs text-gray-500">
                      No team agents available
                    </div>
                  ) : (
                    availableAgents.map(agent => (
                      <DropdownMenuCheckboxItem
                        key={agent.id}
                        checked={agentFilters.includes(agent.id)}
                        onCheckedChange={() => handleAgentToggle(agent.id)}
                      >
                        {agent.name}
                      </DropdownMenuCheckboxItem>
                    ))
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuCheckboxItem
                    checked={includeUnassigned}
                    onCheckedChange={checked =>
                      setIncludeUnassigned(checked !== false)
                    }
                  >
                    Include unassigned
                  </DropdownMenuCheckboxItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7"
                disabled
              >
                {agentFilters.length > 0
                  ? `Selected (${agentFilters.length})`
                  : 'All agents'}
              </Button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-gray-600">
              Contacts
            </span>
            {isClientReady ? (
              <DropdownMenu
                open={isContactDropdownOpen}
                onOpenChange={handleContactDropdownOpenChange}
              >
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7"
                  >
                    {contactFilters.length > 0
                      ? `Selected (${contactFilters.length})`
                      : 'All contacts'}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-72 p-0">
                  <DropdownMenuLabel className="px-2 py-1.5 text-xs text-gray-500">
                    Filter by contacts
                  </DropdownMenuLabel>
                  <div className="px-2 pb-2">
                    <Input
                      value={contactPickerSearch}
                      onChange={event =>
                        setContactPickerSearch(event.target.value)
                      }
                      placeholder="Search by name, phone, email, or ID"
                      className="h-8 text-sm"
                      onKeyDown={event => event.stopPropagation()}
                    />
                  </div>
                  <DropdownMenuSeparator />
                  <div className="max-h-60 overflow-y-auto py-1">
                    {isFetchingRemoteContacts ? (
                      <div className="flex items-center gap-2 px-2 py-2 text-xs text-gray-500">
                        <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                        Searching contacts…
                      </div>
                    ) : null}
                    {contactSearchErrorMessage ? (
                      <div className="px-2 py-2 text-xs text-red-600">
                        {contactSearchErrorMessage}
                      </div>
                    ) : null}
                    {remoteContactOptions.length > 0 ? (
                      <div className="py-1">
                        <div className="px-2 pb-1 text-xs font-semibold text-gray-500">
                          {hasContactPickerSearch
                            ? 'Matching contacts'
                            : 'Contacts'}
                        </div>
                        {remoteContactOptions.map(renderContactOption)}
                      </div>
                    ) : null}
                    {selectedFallbackOptions.length > 0 ? (
                      <div className="py-1">
                        <div className="px-2 pb-1 text-xs font-semibold text-gray-500">
                          Selected contacts
                        </div>
                        {selectedFallbackOptions.map(renderContactOption)}
                      </div>
                    ) : null}
                    {recentFallbackOptions.length > 0 ? (
                      <div className="py-1">
                        <div className="px-2 pb-1 text-xs font-semibold text-gray-500">
                          Recent contacts
                        </div>
                        {recentFallbackOptions.map(renderContactOption)}
                      </div>
                    ) : null}
                    {remoteContactOptions.length === 0 &&
                    selectedFallbackOptions.length === 0 &&
                    recentFallbackOptions.length === 0 &&
                    !isFetchingRemoteContacts &&
                    !contactSearchErrorMessage ? (
                      <div className="px-2 py-2 text-xs text-gray-500">
                        {hasContactPickerSearch
                          ? `No contacts match "${trimmedContactPickerSearch}".`
                          : 'No contacts available'}
                      </div>
                    ) : null}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7"
                disabled
              >
                {contactFilters.length > 0
                  ? `Selected (${contactFilters.length})`
                  : 'All contacts'}
              </Button>
            )}
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            <div className="space-y-1">
              <label
                htmlFor={`${teamKey}-filter-date-from`}
                className="text-xs font-semibold text-gray-600"
              >
                From
              </label>
              <Input
                id={`${teamKey}-filter-date-from`}
                type="date"
                value={dateFrom}
                onChange={event => setDateFromValue(event.target.value)}
                className="h-8 text-sm"
              />
            </div>
            <div className="space-y-1">
              <label
                htmlFor={`${teamKey}-filter-date-to`}
                className="text-xs font-semibold text-gray-600"
              >
                To
              </label>
              <Input
                id={`${teamKey}-filter-date-to`}
                type="date"
                value={dateTo}
                onChange={event => setDateToValue(event.target.value)}
                className="h-8 text-sm"
              />
            </div>
          </div>

          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>
              {activeFilterCount > 0
                ? `${activeFilterCount} active filter${activeFilterCount === 1 ? '' : 's'}`
                : 'Showing all sessions'}
            </span>
            {!includeUnassigned && (
              <span className="text-xs text-gray-600">
                Unassigned sessions hidden
              </span>
            )}
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 md:max-h-[calc(100vh-14rem)]">
        {status === 'loading' ? (
          <div className="flex flex-col items-center justify-center py-12 text-sm text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin text-blue-500 mb-2" />
            Loading assigned sessions…
          </div>
        ) : status === 'error' ? (
          <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-600">
            <p className="font-medium">Unable to load sessions</p>
            <p className="mt-1">
              {(error as Error)?.message ?? 'Please try again later.'}
            </p>
            <button
              type="button"
              onClick={() => {
                refetch().catch(fetchError => {
                  console.error(
                    'Failed to refresh assigned sessions:',
                    fetchError
                  )
                })
              }}
              className="mt-3 inline-flex items-center rounded-md border border-red-300 bg-white px-3 py-1 text-xs font-medium text-red-600 hover:bg-red-100"
            >
              Retry
            </button>
          </div>
        ) : displaySessions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-sm text-gray-500">
            <ShieldCheck className="h-6 w-6 text-blue-400 mb-2" />
            No active chat sessions assigned yet.
          </div>
        ) : (
          <>
            {displaySessions.map(session => {
              const PlatformIcon = getPlatformIcon(session.platform)
              const timeDisplay = formatRelativeTime(session.lastMessageAt)
              const isMine =
                currentAgentId != null &&
                session.assignedAgentId === currentAgentId
              const senderLabel = getSenderLabel(session.lastMessageSender)
              const shouldPrefixSender =
                senderLabel &&
                session.lastMessagePreview &&
                session.lastMessagePreview !== 'No recent messages'

              return (
                <button
                  key={session.id}
                  type="button"
                  onClick={() => handleSessionClick(session.id)}
                  className={cn(
                    'w-full rounded-lg border p-3 text-left transition focus:outline-none focus:ring-2 focus:ring-blue-500',
                    'border-gray-200 hover:border-blue-300 hover:bg-blue-50/60',
                    isMine ? 'border-blue-300 bg-blue-50' : 'bg-white'
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {session.displayName || 'Guest'}
                      </p>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                        <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5">
                          <PlatformIcon className="h-3.5 w-3.5 text-gray-600" />
                          {formatPlatformLabel(session.platform)}
                        </span>
                        <span className="inline-flex items-center gap-1 text-gray-500">
                          <MessageSquare className="h-3.5 w-3.5 text-gray-400" />
                          {session.assignedAgentName
                            ? session.assignedAgentName
                            : 'Unassigned'}
                        </span>
                        <span className="inline-flex items-center gap-1 text-gray-500">
                          <Globe className="h-3.5 w-3.5 text-gray-400" />
                          {session.country ?? 'Unknown'}
                        </span>
                        {session.contactAddress ? (
                          <span className="inline-flex items-center gap-1 text-gray-500">
                            <Phone className="h-3.5 w-3.5 text-gray-400" />
                            {session.contactAddress}
                          </span>
                        ) : (
                          <span className="text-gray-400">No contact ID</span>
                        )}
                      </div>
                    </div>
                    <span className="text-xs text-gray-400">{timeDisplay}</span>
                  </div>
                  <p className="mt-2 text-sm text-gray-600 line-clamp-2">
                    {shouldPrefixSender ? (
                      <>
                        <span className="font-semibold text-gray-700">
                          {senderLabel}
                        </span>
                        {': '}
                      </>
                    ) : null}
                    {session.lastMessagePreview}
                  </p>
                  {isMine && (
                    <div className="mt-2 inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                      Assigned to you
                    </div>
                  )}
                </button>
              )
            })}

            <div ref={loadMoreRef} />

            {isFetchingNextPage && (
              <div className="flex items-center justify-center py-4 text-sm text-gray-500">
                <Loader2 className="mr-2 h-4 w-4 animate-spin text-blue-500" />
                Loading more sessions…
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
